import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  SparklesIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  DocumentTextIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import { apiService } from "../services/apiService";

export type AssistantMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  invoiceId?: string | null;
  summary?: Record<string, unknown> | null;
};

interface AssistantStats {
  invoiceCount?: number;
  pendingTotal?: number;
  overdueTotal?: number;
  revenueTotal?: number;
  clientCount?: number;
  quarterIva?: number;
  quarterIrpf?: number;
  fiscalQuarter?: number;
  unlinkedExpenses?: { count?: number; total?: number };
}

interface FactuAssistantProps {
  open: boolean;
  onClose: () => void;
  selectedExpenseIds?: string[];
  projectId?: string | null;
  activeTab?: string;
  userName?: string | null;
  companyName?: string | null;
  onOpenInvoice?: (invoiceId: string) => void;
  onNavigate?: (tab: string) => void;
}

const TAB_LABELS: Record<string, string> = {
  dashboard: "Panel",
  invoices: "Facturas",
  clients: "Clientes",
  "work-expenses": "Gastos",
  email: "Email",
  settings: "Ajustes",
  "auto-billing": "Auto-facturación",
};

const QUERY_CHIPS = [
  "Conectar tienda WooCommerce",
  "¿Cuánto tengo pendiente de cobro?",
  "Resumen del trimestre (IVA e IRPF)",
  "¿Cuántos gastos sin vincular hay?",
];

type ActionChip = {
  id: string;
  label: string;
  type: "send" | "navigate";
  message?: string;
  tab?: string;
};

const ACTION_CHIPS: ActionChip[] = [
  { id: "create", label: "Crear factura", type: "send", message: "Quiero crear una nueva factura. ¿Qué datos necesitas?" },
  { id: "pending", label: "Ver pendientes", type: "send", message: "¿Cuánto tengo pendiente de cobro?" },
  { id: "expenses", label: "Ir a gastos", type: "navigate", tab: "work-expenses" },
];

function formatGreeting(userName: string | null): string {
  if (!userName) return "Hola";
  if (!userName.includes("@")) {
    const first = userName.trim().split(/\s+/)[0];
    return first ? `Hola ${first}` : "Hola";
  }
  return "Hola";
}

function buildWelcome(
  stats: AssistantStats | null,
  activeTab: string,
  companyName: string | null,
  selectedCount: number,
  greet: string
): string {
  const tabLabel = TAB_LABELS[activeTab] || activeTab;
  const company = companyName ? ` · ${companyName}` : "";
  const expenseHint =
    selectedCount > 0
      ? `\n\nTienes ${selectedCount} gasto(s) seleccionado(s) para facturar.`
      : "";

  if (!stats || stats.invoiceCount == null) {
    return `${greet}, soy tu asistente FactuApp${company}. Estás en **${tabLabel}**.${expenseHint}\n\nPregúntame por facturas, gastos, IVA o pídeme crear una factura.`;
  }

  const inv = stats.invoiceCount ?? 0;
  const pending = Number(stats.pendingTotal ?? 0);
  const unlinked = stats.unlinkedExpenses?.count ?? 0;
  const q = stats.fiscalQuarter ?? Math.floor(new Date().getMonth() / 3) + 1;

  let snapshot = `\n\n**Resumen rápido:** ${inv} factura(s)`;
  if (pending > 0) snapshot += ` · ${pending.toFixed(2)} € pendientes de cobro`;
  if (unlinked > 0) snapshot += ` · ${unlinked} gasto(s) sin vincular`;
  if ((stats.quarterIva ?? 0) > 0) {
    snapshot += `\n**T${q}:** IVA ${Number(stats.quarterIva).toFixed(2)} € · IRPF ${Number(stats.quarterIrpf ?? 0).toFixed(2)} €`;
  }

  if (inv === 0) {
    snapshot +=
      "\n\nAún no hay facturas emitidas. Usa **Crear factura** o ve a la pestaña Facturas para empezar.";
  } else if (pending <= 0 && unlinked > 0) {
    snapshot += "\n\nPuedes vincular gastos desde **Ir a gastos** o pedirme una factura con los seleccionados.";
  }

  return `${greet}, soy tu asistente FactuApp${company}. Vista: **${tabLabel}**.${snapshot}${expenseHint}\n\nEscribe tu pregunta o elige una acción rápida.`;
}

const ASSISTANT_UNAVAILABLE =
  "El asistente no está disponible en este momento. Inténtelo de nuevo.";

function formatAssistantError(res: { error?: string; message?: string }): string {
  const msg = res.message || res.error || "";
  if (/401|no autorizado|token/i.test(msg)) {
    return "Sesión caducada. Cierra el asistente, vuelve a iniciar sesión e inténtalo de nuevo.";
  }
  if (/NVIDIA|IA no está|503|no configurada|pausa temporal|circuit/i.test(msg)) {
    return "El servicio de IA no está disponible. Revisa Ajustes → Inteligencia artificial o contacta al administrador.";
  }
  if (/tardó|504|timeout/i.test(msg)) {
    return "La IA tardó demasiado en responder. Prueba una pregunta más corta.";
  }
  if (/Límite|429|demasiadas|Espere un momento/i.test(msg)) {
    return "Has alcanzado el límite de peticiones al asistente. Espera unos minutos.";
  }
  return msg || ASSISTANT_UNAVAILABLE;
}

function renderMarkdownLite(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={i} className="font-semibold text-zinc-100">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

export const FactuAssistant: React.FC<FactuAssistantProps> = ({
  open,
  onClose,
  selectedExpenseIds = [],
  projectId = null,
  activeTab = "dashboard",
  userName = null,
  companyName = null,
  onOpenInvoice,
  onNavigate,
}) => {
  const greet = useMemo(() => formatGreeting(userName), [userName]);

  const tenantKey = useMemo(
    () => `${userName || ""}:${companyName || ""}`,
    [userName, companyName]
  );

  const queryChips = useMemo(() => {
    const chips = [...QUERY_CHIPS];
    if (activeTab === "work-expenses" && selectedExpenseIds.length === 0) {
      chips.unshift("¿Cómo vinculo gastos a una factura?");
    }
    if (activeTab === "invoices") {
      chips.unshift("¿Cuál es mi última factura emitida?");
    }
    return chips.slice(0, 4);
  }, [activeTab, selectedExpenseIds.length]);

  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [input, setInput] = useState("");
  const [processing, setProcessing] = useState(false);
  const [stats, setStats] = useState<AssistantStats | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const welcomedRef = useRef(false);
  const sessionRef = useRef(0);

  useEffect(() => {
    setMessages([]);
    setInput("");
    setStats(null);
    welcomedRef.current = false;
    sessionRef.current += 1;
  }, [tenantKey]);

  const loadWelcome = useCallback(async () => {
    const session = ++sessionRef.current;
    let loadedStats: AssistantStats | null = null;
    try {
      const res = await apiService.getAssistantStats(projectId || undefined);
      if (!res.error && res.data?.stats) {
        loadedStats = res.data.stats as AssistantStats;
        if (session === sessionRef.current) setStats(loadedStats);
      }
    } catch {
      /* stats opcionales */
    }
    if (session !== sessionRef.current) return;
    const welcome = buildWelcome(
      loadedStats,
      activeTab,
      companyName,
      selectedExpenseIds.length,
      greet
    );
    setMessages([{ id: "welcome", role: "assistant", content: welcome }]);
  }, [activeTab, companyName, greet, projectId, selectedExpenseIds.length]);

  useEffect(() => {
    if (open && !welcomedRef.current) {
      welcomedRef.current = true;
      void loadWelcome();
    }
    if (!open) {
      welcomedRef.current = false;
      sessionRef.current += 1;
    }
  }, [open, loadWelcome]);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 220);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, processing]);

  const sendMessage = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || processing) return;

    const userMsg: AssistantMessage = {
      id: `u-${Date.now()}`,
      role: "user",
      content: trimmed,
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setProcessing(true);

    try {
      const res = await apiService.assistantChat(trimmed, {
        selectedExpenseIds,
        projectId: projectId || undefined,
        activeTab,
      });

      if (res.error || !res.data) {
        setMessages((prev) => [
          ...prev,
          {
            id: `e-${Date.now()}`,
            role: "assistant",
            content: formatAssistantError(res),
          },
        ]);
        return;
      }

      const data = res.data as {
        success?: boolean;
        message?: string;
        reply?: string;
        invoiceId?: string;
        summary?: Record<string, unknown>;
        navigateTo?: string | null;
        intent?: string;
        aiUnavailable?: boolean;
      };

      const textReply = (data.message || data.reply || "").trim();
      const assistantMsg: AssistantMessage = {
        id: `a-${Date.now()}`,
        role: "assistant",
        content:
          textReply ||
          (data.aiUnavailable || data.success === false
            ? ASSISTANT_UNAVAILABLE
            : "No he recibido una respuesta del asistente. Inténtalo de nuevo."),
        invoiceId: data?.invoiceId || null,
        summary: data?.summary || null,
      };
      setMessages((prev) => [...prev, assistantMsg]);

      if (data.navigateTo && onNavigate && TAB_LABELS[data.navigateTo]) {
        onNavigate(data.navigateTo);
      }
    } catch (err) {
      console.error("[FactuAssistant] sendMessage:", err);
      setMessages((prev) => [
        ...prev,
        {
          id: `e-${Date.now()}`,
          role: "assistant",
          content: ASSISTANT_UNAVAILABLE,
        },
      ]);
    } finally {
      setProcessing(false);
    }
  };

  const handleActionChip = (chip: ActionChip) => {
    if (chip.type === "navigate" && chip.tab && onNavigate) {
      onNavigate(chip.tab);
      if (chip.message) void sendMessage(chip.message);
      return;
    }
    if (chip.message) void sendMessage(chip.message);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void sendMessage(input);
  };

  const showEmptyHint = messages.length <= 1 && !processing;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-50 bg-black/25 dark:bg-black/45 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            aria-hidden
          />
          <motion.aside
            role="dialog"
            aria-label="Asistente FactuApp"
            className="fixed top-0 right-0 z-[60] h-full w-full max-w-[400px] flex flex-col font-[system-ui,-apple-system,BlinkMacSystemFont,'SF Pro Text','Segoe UI',sans-serif] border-l border-white/20 dark:border-zinc-700/50 bg-white/80 dark:bg-zinc-950/88 backdrop-blur-2xl shadow-[0_0_60px_rgba(0,0,0,0.12)] dark:shadow-[0_0_80px_rgba(0,0,0,0.45)]"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 340 }}
          >
            <header className="flex items-center gap-3 px-5 h-[52px] border-b border-zinc-200/80 dark:border-zinc-800/80 shrink-0">
              <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-md shadow-blue-500/25">
                <SparklesIcon className="h-[18px] w-[18px] text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[15px] font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
                  FactuApp
                </p>
                <p className="text-[11px] text-zinc-500 dark:text-zinc-400 truncate">
                  Asistente · {TAB_LABELS[activeTab] || activeTab}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-2 rounded-full text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 dark:text-zinc-400 transition-colors"
                aria-label="Cerrar asistente"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </header>

            {stats && (stats.invoiceCount ?? 0) > 0 && (
              <div className="px-4 py-2 flex gap-2 overflow-x-auto scrollbar-none border-b border-zinc-100 dark:border-zinc-800/60">
                <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-zinc-100 dark:bg-zinc-900 px-2.5 py-1 text-[10px] font-medium text-zinc-600 dark:text-zinc-400">
                  <ChartBarIcon className="h-3 w-3" />
                  {stats.invoiceCount} facturas
                </span>
                {(stats.pendingTotal ?? 0) > 0 && (
                  <span className="shrink-0 rounded-full bg-amber-100 dark:bg-amber-950/50 px-2.5 py-1 text-[10px] font-medium text-amber-800 dark:text-amber-300">
                    {Number(stats.pendingTotal).toFixed(0)} € pend.
                  </span>
                )}
              </div>
            )}

            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto px-4 py-4 space-y-3 scroll-smooth"
            >
              {messages.map((msg) => (
                <motion.div
                  key={msg.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed ${
                      msg.role === "user"
                        ? "bg-blue-600 text-white rounded-br-md shadow-sm"
                        : "bg-zinc-100/90 dark:bg-zinc-900/90 border border-zinc-200/60 dark:border-zinc-800 text-zinc-800 dark:text-zinc-200 rounded-bl-md"
                    }`}
                  >
                    <p className="whitespace-pre-wrap">
                      {msg.role === "assistant"
                        ? renderMarkdownLite(msg.content)
                        : msg.content}
                    </p>
                    {msg.summary && (
                      <div className="mt-2 pt-2 border-t border-zinc-300/50 dark:border-zinc-700/60 text-xs text-zinc-500 dark:text-zinc-400 space-y-0.5">
                        {msg.summary.number != null && <p>Nº {String(msg.summary.number)}</p>}
                        {msg.summary.total != null && (
                          <p>Total: {Number(msg.summary.total).toFixed(2)} €</p>
                        )}
                      </div>
                    )}
                    {msg.invoiceId && onOpenInvoice && (
                      <button
                        type="button"
                        onClick={() => onOpenInvoice(msg.invoiceId!)}
                        className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        <DocumentTextIcon className="h-4 w-4" />
                        Abrir en Facturas
                      </button>
                    )}
                  </div>
                </motion.div>
              ))}

              {processing && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="rounded-2xl rounded-bl-md bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 px-4 py-3 flex items-center gap-2.5">
                    <span className="flex gap-1">
                      {[0, 1, 2].map((i) => (
                        <motion.span
                          key={i}
                          className="h-1.5 w-1.5 rounded-full bg-blue-500"
                          animate={{ opacity: [0.25, 1, 0.25], scale: [1, 1.15, 1] }}
                          transition={{ repeat: Infinity, duration: 0.85, delay: i * 0.12 }}
                        />
                      ))}
                    </span>
                    <span className="text-xs text-zinc-500 dark:text-zinc-400">Pensando…</span>
                  </div>
                </motion.div>
              )}

              {showEmptyHint && messages.length === 0 && (
                <p className="text-center text-sm text-zinc-400 py-8">Cargando tu resumen…</p>
              )}
            </div>

            <div className="shrink-0 border-t border-zinc-200/80 dark:border-zinc-800 px-3 pt-2 pb-3 safe-area-pb">
              <div className="flex gap-1.5 overflow-x-auto pb-2 scrollbar-none">
                {ACTION_CHIPS.map((chip) => (
                  <button
                    key={chip.id}
                    type="button"
                    disabled={processing}
                    onClick={() => handleActionChip(chip)}
                    className="shrink-0 rounded-full bg-blue-50 dark:bg-blue-950/40 border border-blue-200/60 dark:border-blue-800/50 px-3 py-1.5 text-[11px] font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 disabled:opacity-50 transition-colors"
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-1.5 overflow-x-auto pb-2 scrollbar-none">
                {queryChips.map((chip) => (
                  <button
                    key={chip}
                    type="button"
                    disabled={processing}
                    onClick={() => {
                      if (chip === "Conectar tienda WooCommerce" && onNavigate) {
                        onNavigate("settings");
                        return;
                      }
                      void sendMessage(chip);
                    }}
                    className="shrink-0 rounded-full border border-zinc-200 dark:border-zinc-700 bg-white/60 dark:bg-zinc-900/60 px-3 py-1 text-[11px] text-zinc-600 dark:text-zinc-400 hover:border-zinc-300 dark:hover:border-zinc-600 disabled:opacity-50"
                  >
                    {chip}
                  </button>
                ))}
              </div>

              <form onSubmit={handleSubmit} className="flex gap-2 items-end">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      void sendMessage(input);
                    }
                  }}
                  rows={1}
                  placeholder="Mensaje"
                  className="flex-1 resize-none rounded-full border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-900 px-4 py-2.5 text-[13px] text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 max-h-24"
                  disabled={processing}
                />
                <button
                  type="submit"
                  disabled={processing || !input.trim()}
                  className="shrink-0 h-10 w-10 flex items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-35 shadow-sm transition-colors"
                  aria-label="Enviar"
                >
                  <PaperAirplaneIcon className="h-4 w-4" />
                </button>
              </form>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default FactuAssistant;
